import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { Button, toast, cn } from '@mlm/ui';

/**
 * Icon-only copy-to-clipboard button. Swaps to a check for ~1.6s and fires a
 * toast on success so members get clear feedback (e.g. copying a tracking
 * number to paste into the courier site).
 */
export function CopyButton({
  value,
  label = 'Copied',
  title = 'Copy',
  className,
}: {
  value: string;
  /** Toast message on success. */
  label?: string;
  /** Accessible label / tooltip. */
  title?: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  async function copy(e: React.MouseEvent) {
    // The button often lives inside other click targets (order card row).
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(label);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error('Could not copy');
    }
  }

  return (
    <Button
      type="button"
      size="icon"
      variant="ghost"
      onClick={copy}
      title={title}
      aria-label={title}
      className={cn('h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground', className)}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  );
}
